import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Typography } from '@mui/material';
import * as _ from 'lodash';

import OnboardForm, { formClasses } from './OnboardForm';
import Name2 from '../form/Name2';
import TextInput2 from '../form/TextInput2';
import validationUtils from '../../services/validationUtils';
import PhoneInput2 from '../form/PhoneInput2';
import CheckboxInput2 from '../form/CheckboxInput2';
import { MessagingMethod } from '../../data/models/messaging-method.model';
import SelectMenu2 from '../form/SelectMenu2';

interface ContactFormProps {
  messagingMethods: MessagingMethod[];
  defaultValues: any;
  onSubmit: (values: any) => void;
}

const ContactForm = forwardRef(
  ({ messagingMethods, defaultValues, onSubmit }: ContactFormProps, ref) => {
    const { t } = useTranslation('common');
    const requiredFieldMessage = t('common.requiredField');

    const { control, formState, watch, handleSubmit, setValue, getValues } =
      useForm({
        defaultValues: _.pick(defaultValues, [
          'OwnerFirstName',
          'OwnerLastName',
          'OwnerEmailAddress',
          'OwnerPhone',
          'OwnerPhoneExtension',
          'MainPhone',
          'MainPhoneExtension',
          'BillingContactFirstName',
          'BillingContactLastName',
          'BillingEmailAddress',
          'BillingPhone',
          'BillingPhoneExtension',
          'MainContactFirstName',
          'MainContactLastName',
          'EmailAddress',
          'DispatchPhone',
          'DispatchPhoneExtension',
          'MessagingMethodID',
          'DefaultDriverMessagingMethodID',
          'BillingSameAsOwner',
          'DispatchSameAsOwner',
        ]),
      });

    const billingSame = watch('BillingSameAsOwner');
    const dispatchSame = watch('DispatchSameAsOwner');

    useEffect(() => {
      if (billingSame) {
        setValue('BillingPhone', watch('OwnerPhone'));
        setValue('BillingPhoneExtension', watch('OwnerPhoneExtension'));
        setValue('BillingEmailAddress', watch('OwnerEmailAddress'));
        setValue('BillingContactFirstName', watch('OwnerFirstName'));
        setValue('BillingContactLastName', watch('OwnerLastName'));
      }
    }, [billingSame]);

    useEffect(() => {
      if (dispatchSame) {
        setValue('DispatchPhone', watch('OwnerPhone'));
        setValue('DispatchPhoneExtension', watch('OwnerPhoneExtension'));
        setValue('EmailAddress', watch('OwnerEmailAddress'));
        setValue('MainContactFirstName', watch('OwnerFirstName'));
        setValue('MainContactLastName', watch('OwnerLastName'));
      }
    }, [dispatchSame]);

    useImperativeHandle(ref, () => ({
      submitForm: handleSubmit(onSubmit),
      getValues,
    }));

    return (
      <OnboardForm title={t('onboard.contactInfo')}>
        <Typography className={formClasses.subtitle}>
          {t('onboard.owner')}
        </Typography>
        <Name2
          className={`${formClasses.control} ${formClasses.wide}`}
          namePrefix="Owner"
          control={control}
          formState={formState}
          firstNameLabel={t('onboard.firstName') || ''}
          lastNameLabel={t('onboard.lastName') || ''}
          rules={{
            required: requiredFieldMessage,
          }}
          required
        />
        <TextInput2
          label={t('onboard.email')}
          name="OwnerEmailAddress"
          className={formClasses.control}
          required
          control={control}
          errors={formState.errors}
          rules={{
            required: requiredFieldMessage,
            pattern: validationUtils.emailValidation(),
          }}
          inputProps={{ maxLength: 255 }}
        />
        <PhoneInput2
          label={t('onboard.phone')}
          name="OwnerPhone"
          className={`${formClasses.control} ${formClasses.wide}`}
          extensionName="OwnerPhoneExtension"
          required
          control={control}
          errors={formState.errors}
        />
        <PhoneInput2
          label={t('provider.mainPhone')}
          name="MainPhone"
          className={`${formClasses.control} ${formClasses.wide}`}
          extensionName="MainPhoneExtension"
          required
          control={control}
          errors={formState.errors}
        />
        <Typography className={formClasses.subtitle}>
          {t('onboard.billing')}
        </Typography>
        <CheckboxInput2
          label={t('onboard.sameAsOwner')}
          name="BillingSameAsOwner"
          className={formClasses.checkbox}
          required={false}
          control={control}
        />
        {!billingSame && (
          <>
            <Name2
              className={`${formClasses.control} ${formClasses.wide}`}
              namePrefix="BillingContact"
              control={control}
              formState={formState}
              firstNameLabel={t('onboard.firstName') || ''}
              lastNameLabel={t('onboard.lastName') || ''}
              rules={{
                required: requiredFieldMessage,
              }}
              required
            />
            <TextInput2
              label={t('onboard.email')}
              name="BillingEmailAddress"
              className={formClasses.control}
              control={control}
              errors={formState.errors}
              rules={{
                required: requiredFieldMessage,
                pattern: validationUtils.emailValidation(),
              }}
              inputProps={{ maxLength: 255 }}
              required
            />
            <PhoneInput2
              label={t('onboard.phone')}
              name="BillingPhone"
              className={`${formClasses.control} ${formClasses.wide}`}
              extensionName="BillingPhoneExtension"
              control={control}
              errors={formState.errors}
              required
            />
          </>
        )}
        <Typography className={formClasses.subtitle}>
          {t('onboard.dispatch')}
        </Typography>
        <CheckboxInput2
          label={t('onboard.sameAsOwner')}
          name="DispatchSameAsOwner"
          className={formClasses.checkbox}
          required={false}
          control={control}
        />
        {!dispatchSame && (
          <>
            <Name2
              className={`${formClasses.control} ${formClasses.wide}`}
              namePrefix="MainContact"
              control={control}
              formState={formState}
              firstNameLabel={t('onboard.firstName') || ''}
              lastNameLabel={t('onboard.lastName') || ''}
              rules={{
                required: requiredFieldMessage,
              }}
              required
            />
            <TextInput2
              label={t('onboard.email')}
              name="EmailAddress"
              className={formClasses.control}
              control={control}
              errors={formState.errors}
              rules={{
                required: requiredFieldMessage,
                pattern: validationUtils.emailValidation(),
              }}
              inputProps={{ maxLength: 255 }}
              required
            />
            <PhoneInput2
              label={t('onboard.phone')}
              name="DispatchPhone"
              className={`${formClasses.control} ${formClasses.wide}`}
              extensionName="DispatchPhoneExtension"
              control={control}
              errors={formState.errors}
              required
            />
          </>
        )}
        <Typography className={formClasses.subtitle}>
          {t('onboard.messaging.title')}
        </Typography>
        <SelectMenu2
          label={t('onboard.messagingMethod')}
          name="MessagingMethodID"
          className={formClasses.control}
          values={messagingMethods}
          valueAttr="messagingMethodID"
          labelAttr="messagingMethodName"
          required={true}
          control={control}
          errors={formState.errors}
          rules={{ required: requiredFieldMessage }}
        />
        <SelectMenu2
          label={t('onboard.driverMessagingMethod')}
          name="DefaultDriverMessagingMethodID"
          className={formClasses.control}
          values={messagingMethods}
          valueAttr="messagingMethodID"
          labelAttr="messagingMethodName"
          required={true}
          control={control}
          errors={formState.errors}
          rules={{ required: requiredFieldMessage }}
        />
        <Typography className={formClasses.disclaimer}>
          {t('onboard.messaging.disclaimer')}
        </Typography>
        <Typography className={formClasses.disclaimer}>
          {t('onboard.messaging.data')}
        </Typography>
        <Typography className={formClasses.disclaimer}>
          {t('onboard.messaging.rate')}
        </Typography>
        <Typography className={formClasses.disclaimer}>
          {t('onboard.messaging.optOut')}
        </Typography>
        <Typography className={formClasses.disclaimer}>
          <a href="https://americanlogistics.com/privacy/" target="_blank">
            {t('onboard.messaging.privacyLink')}
          </a>
        </Typography>
        <Typography className={formClasses.disclaimer}>
          <a href="https://americanlogistics.com/legal/" target="_blank">
            {t('onboard.messaging.termsLink')}
          </a>
        </Typography>
      </OnboardForm>
    );
  },
);

export default ContactForm;
